import React, { useEffect, useMemo, useState } from 'react';
import {
  Tabs,
  Table,
  Button,
  Modal,
  Select,
  Tag,
  Alert,
  Space,
  Typography,
  Form,
  message,
} from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import apiService from '../services/apiService';
import { userService } from '../services/userService';

const { Text } = Typography;

const LEAD_STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'converted', label: 'Converted' },
  { value: 'lost', label: 'Lost' },
];

const DEAL_STAGE_OPTIONS = [
  { value: 'opportunity_qualified', label: 'Opportunity Qualified' },
  { value: 'proposal_in_progress', label: 'Proposal in Progress' },
  { value: 'poc', label: 'Proof of Concept (POC)' },
  { value: 'proposal_submitted', label: 'Proposal Submitted' },
  { value: 'confirmed_commit', label: 'Confirmed Commit' },
  { value: 'closed_won', label: 'Closed Won' },
  { value: 'closed_lost', label: 'Closed Lost' },
  { value: 'closed_dropped', label: 'Closed Dropped' },
];

const LegacyTable = ({ type, refreshKey, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [users, setUsers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const valueOptions = type === 'deal' ? DEAL_STAGE_OPTIONS : LEAD_STATUS_OPTIONS;
  const valueLabel = type === 'deal' ? 'Stage' : 'Status';
  const legacyField = type === 'deal' ? 'legacyStageBefore' : 'legacyStatusBefore';
  const valueField = type === 'deal' ? 'stage' : 'status';
  const titleField = type === 'deal' ? 'title' : null;

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await apiService.get('/admin/legacy', { params: { type } });
      setRows(res.data?.data || []);
      setSelectedKeys([]);
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to load legacy records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [type, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    userService
      .getUsers({ limit: 200 })
      .then((res) => {
        // Tolerate the various shapes the users endpoint may return.
        const candidates = [
          res?.data?.users,
          res?.data?.data?.users,
          res?.data?.data,
          res?.data,
          res?.users,
          res,
        ];
        const list = candidates.find((c) => Array.isArray(c)) || [];
        setUsers(list);
      })
      .catch(() => setUsers([]));
  }, []);

  const columns = useMemo(() => {
    const base = [
      {
        title: type === 'deal' ? 'Deal Title' : 'Name',
        key: 'identity',
        render: (_, r) =>
          type === 'deal'
            ? r[titleField] || '—'
            : `${r.firstName || ''} ${r.lastName || ''}`.trim() || r.email,
      },
      {
        title: `Original ${valueLabel}`,
        dataIndex: legacyField,
        render: (v) => <Tag color="orange">{v || '—'}</Tag>,
      },
      {
        title: `Current ${valueLabel}`,
        dataIndex: valueField,
        render: (v) => <Tag>{v}</Tag>,
      },
      {
        title: 'Assigned To',
        dataIndex: 'assignedTo',
        render: (u) =>
          u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email : <Text type="secondary">Unassigned</Text>,
      },
    ];
    if (type === 'lead') {
      base.splice(1, 0, { title: 'Email', dataIndex: 'email' });
      base.splice(2, 0, { title: 'Company', dataIndex: 'company' });
    }
    return base;
  }, [type, legacyField, valueField, valueLabel, titleField]);

  const handleBulkSave = async () => {
    try {
      const values = await form.validateFields();
      if (!values.assignedTo && !values.newValue && !values.clearLegacy) {
        message.warning('Pick at least one change to apply.');
        return;
      }
      setSaving(true);
      await apiService.post('/admin/legacy/reassign', {
        type,
        ids: selectedKeys,
        ...values,
      });
      message.success(`Updated ${selectedKeys.length} record(s)`);
      setModalOpen(false);
      form.resetFields();
      fetchData();
      onChanged?.();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err.response?.data?.message || 'Bulk update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <Text type="secondary">
          {rows.length === 0
            ? 'Nothing parked here. Records are added automatically when migrated from retired statuses/stages.'
            : `${rows.length} record(s) waiting for triage. Select rows then click "Reassign / update".`}
        </Text>
        <Space>
          <Button onClick={fetchData} loading={loading}>Refresh</Button>
          <Button
            type="primary"
            icon={<TeamOutlined />}
            disabled={selectedKeys.length === 0}
            onClick={() => {
              form.resetFields();
              form.setFieldsValue({ clearLegacy: true });
              setModalOpen(true);
            }}
          >
            Reassign / update ({selectedKeys.length})
          </Button>
        </Space>
      </div>
      <Table
        rowKey="_id"
        size="small"
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={{ pageSize: 10 }}
        rowSelection={{
          selectedRowKeys: selectedKeys,
          onChange: setSelectedKeys,
        }}
      />
      <Modal
        title={`Bulk update ${selectedKeys.length} ${type}(s)`}
        open={modalOpen}
        onOk={handleBulkSave}
        onCancel={() => setModalOpen(false)}
        okText="Apply"
        confirmLoading={saving}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          className="mb-3"
          message="All selected records will receive the values you set below. Leave a field blank to skip."
        />
        <Form form={form} layout="vertical">
          <Form.Item name="assignedTo" label="Reassign to salesperson">
            <Select
              showSearch
              allowClear
              placeholder="Pick a user"
              optionFilterProp="label"
              options={users.map((u) => ({
                value: u._id,
                label: `${u.firstName || ''} ${u.lastName || ''} (${u.email})`.trim(),
              }))}
            />
          </Form.Item>
          <Form.Item name="newValue" label={`Set new ${valueLabel.toLowerCase()}`}>
            <Select allowClear placeholder={`Pick a ${valueLabel.toLowerCase()}`} options={valueOptions} />
          </Form.Item>
          <Form.Item name="clearLegacy" label="After triage" valuePropName="checked" tooltip="Clears the legacy marker so the record disappears from this list.">
            <Select
              defaultValue={true}
              options={[
                { value: true, label: 'Mark as triaged (remove from this list)' },
                { value: false, label: 'Keep in legacy list' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

const LegacyRecordsSettings = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  return (
    <div className="space-y-4">
      <div>
        <Text strong className="text-lg">Legacy Records</Text>
        <div className="text-sm text-gray-500">
          Records that were sitting on retired statuses/stages have been parked on a safe default
          (Contacted for leads, Opportunity Qualified for deals). Reassign them to a salesperson and
          set the right new value below.
        </div>
      </div>
      <Tabs
        defaultActiveKey="leads"
        items={[
          { key: 'leads', label: 'Leads', children: <LegacyTable type="lead" refreshKey={refreshKey} onChanged={() => setRefreshKey((k) => k + 1)} /> },
          { key: 'deals', label: 'Deals', children: <LegacyTable type="deal" refreshKey={refreshKey} onChanged={() => setRefreshKey((k) => k + 1)} /> },
        ]}
      />
    </div>
  );
};

export default LegacyRecordsSettings;
